// Клиент API. В Telegram подписывает каждый запрос данными запуска (initData).

import { initData, inTelegram } from './tg.ts';
import { dropSession, onSite, sessionToken } from './web.ts';

export type Form = 'classic' | 'airy' | 'comp' | 'mono';
export type Gamma = 'pastel' | 'warm' | 'white' | 'bright' | 'deep';
export type Size = 'S' | 'M' | 'L';

export interface Taste {
  formsLike: Form[];
  formsDislike: Form[];
  gammasLike: Gamma[];
  gammasDislike: Gamma[];
  flowersLike: string[];
  flowersDislike: string[];
}

export interface Design {
  id: string;
  name: string;
  gamma: Gamma;
  form: Form;
  description: string;
  flowers: string[];
  colors: string[];
  photoUrl: string | null;
  sizes: Array<{ size: Size; price: number; available: boolean }>;
}

export interface PickT {
  role: 'best' | 'second' | 'bolder' | 'third';
  price: number;
  design: Design;
}

export interface Slot {
  from: number;
  to: number;
  available: boolean;
  nearest: boolean;
  full?: boolean;
}

export interface OrderView {
  publicId: string;
  kind: 'self' | 'gift' | 'surprise' | 'individual';
  status: string;
  statusText: string;
  designId: string | null;
  designName: string | null;
  size: Size;
  itemPrice: number;
  deliveryPrice: number | null;
  total: number;
  date: string | null;
  slotFrom: number | null;
  slotTo: number | null;
  address: string | null;
  zone: number | null;
  recipientMode: 'ask' | 'known' | null;
  recipientName: string | null;
  personId: number | null;
  occasion: string | null;
  cardText: string | null;
  hasPhoto: boolean;
  photoUrl: string | null;
  fixRequest: string | null;
  liked: boolean | null;
  recipientLinks: { telegram: string; web: string } | null;
  /** Клиент попросил нас самих связаться с получателем (анонимно). */
  contactByUs?: boolean;
  discount?: number;
  surcharge?: number | null;
  payLink?: string | null;
  paymentMode?: 'tbank' | 'dev' | 'manual';
  createdAt: string;
  handover?: 'hand' | 'door';
  addressExtra?: string | null;
  lat?: number | null;
  lon?: number | null;
  recipientPhone?: string | null;
  cardSigned?: boolean;
  surpriseNotes?: string | null;
  payDeadline?: string | null;
  cancelReason?: string | null;
  cancelledBy?: 'client' | 'staff' | 'system' | null;
  refundStatus?: 'needed' | 'done' | null;
  offerText?: string | null;
  canCancel?: boolean;
  /** Не вручили: клиент выбирает новое время; price — повторная доставка (0 — за наш счёт). */
  redelivery?: { price: number; reason: string | null } | null;
  /** Доплата — за повторную доставку, а не за МКАД. */
  redeliveryPending?: boolean;
}

export interface Me {
  user: { firstName: string | null; phone: string | null; consentPd: boolean; consentAds: boolean; notifyOrders: boolean; email?: string | null; telegram?: boolean };
  taste: Taste | null;
  people: Array<{ id: number; name: string; relation: string }>;
  dates: Array<{ id: number; personId: number | null; personName: string | null; occasion: string; month: number; day: number; remindDays: number; year: number | null }>;
  orders: OrderView[];
  promos: PromoT[];
}

/** Начисленный промокод. ok/reason/discount — только в ответе для конкретной корзины. */
export interface PromoT {
  id: number;
  title: string;
  kind: 'free' | 'percent' | 'rub' | 'individual';
  expiresAt: string | null;
  until: string;
  ok?: boolean;
  reason?: string;
  discount?: number;
}

export interface AppConfig {
  bot: string;
  delivery: { zone1: number | null; zone2: number | null; zone3: number | null; giftFlat: number | null };
  shop: { openHour: number; closeHour: number; assemblyMinutes: number; windowHours: number; photoAutoApproveMinutes: number };
  addressSuggest: boolean;
  payments: 'tbank' | 'dev' | 'manual';
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const BASE = (import.meta as any).env?.VITE_API_URL ?? '';

async function req<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (inTelegram) headers.Authorization = `tma ${initData()}`;
  else if (onSite) {
    try {
      headers.Authorization = `web ${await sessionToken()}`;
    } catch (e: any) {
      throw new ApiError(0, 'network', e.message);
    }
  } else if (location.hostname === 'localhost') headers['X-Dev-User'] = '1';
  let res: Response;
  try {
    res = await fetch(BASE + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи. Проверь интернет и попробуй ещё раз');
  }
  const data = await res.json().catch(() => ({}));
  // Сессия сайта устарела — заводим новую и повторяем один раз.
  if (res.status === 401 && onSite && !retried) {
    dropSession();
    return req<T>(method, path, body, true);
  }
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Что-то пошло не так. Попробуй ещё раз');
  return data as T;
}

export const api = {
  config: () => req<AppConfig>('GET', '/api/config'),
  catalog: () => req<{ designs: Design[] }>('GET', '/api/catalog'),
  slots: (date?: string) => req<{ today: string; tomorrow: string; date: string; slots: Slot[] }>('GET', `/api/slots${date ? `?date=${date}` : ''}`),
  suggest: (q: string) => req<{ suggestions: Array<{ value: string; lat: number | null; lon: number | null; insideMkad: boolean | null; precise: boolean }> }>('GET', `/api/address/suggest?q=${encodeURIComponent(q)}`),
  me: () => req<Me>('GET', '/api/me'),
  redeemPromo: (code: string) => req<{ promo: { id: number; title: string } }>('POST', '/api/promos/redeem', { code }),
  promosForCart: (p: { kind: string; designId?: string; size: string; date: string }) => req<{ promos: PromoT[] }>('POST', '/api/promos/for-cart', p),
  consent: (ads: boolean) => req<{ consentPd: boolean; consentAds: boolean }>('POST', '/api/me/consent', { ads }),
  updateMe: (p: { phone?: string; notifyOrders?: boolean }) => req('PATCH', '/api/me', p),
  forget: () => req('DELETE', '/api/me/data'),
  saveTaste: (personId: number | null, taste: Taste) => req('POST', '/api/taste', { personId, taste }),
  recommend: (p: { personId: number | null; occasion?: string; size: Size; taste?: Taste; save?: boolean }) => req<{ picks: PickT[]; taste: Taste }>('POST', '/api/recommend', p),
  ranked: (p: { personId: number | null; occasion?: string; size: Size; taste?: Taste }) =>
    req<{ items: Array<{ design: Design; price: number; score: number }> }>('POST', '/api/catalog/ranked', p),
  person: (id: number) => req<{ person: { id: number; name: string; relation: string }; taste: Taste; dates: Me['dates']; orders: OrderView[] }>('GET', `/api/people/${id}`),
  addPerson: (name: string, relation: string) => req<{ id: number; name: string; relation: string }>('POST', '/api/people', { name, relation }),
  addDate: (p: { personId?: number; personName?: string; relation: string; occasion: string; date: string; remindDays: number; yearly: boolean }) => req<{ id: number; personId: number | null }>('POST', '/api/dates', p),
  deleteDate: (id: number) => req('DELETE', `/api/dates/${id}`),
  createOrder: (p: Record<string, unknown>) => req<OrderView>('POST', '/api/orders', p),
  order: (id: string) => req<OrderView>('GET', `/api/orders/${encodeURIComponent(id)}`),
  orderPhoto: (id: string) => req<{ type: string; base64: string }>('GET', `/api/orders/${encodeURIComponent(id)}/photo`),
  pay: (id: string, kind: 'main' | 'surcharge' = 'main') => req<{ paymentId: number; sbpUrl: string | null; paymentUrl: string | null; dev: boolean; manual: boolean }>('POST', `/api/orders/${encodeURIComponent(id)}/pay`, { kind }),
  devPay: (paymentId: number, fail = false) => req('POST', `/api/dev/pay/${paymentId}`, { fail }),
  cancel: (id: string) => req<OrderView>('POST', `/api/orders/${encodeURIComponent(id)}/cancel`, {}),
  redeliverySlots: (id: string, date?: string) => req<{ price: number; reason: string | null; today: string; date: string; slots: Slot[] }>('GET', `/api/orders/${encodeURIComponent(id)}/redelivery${date ? `?date=${date}` : ''}`),
  redelivery: (id: string, p: { date: string; slotFrom: number }) => req<OrderView>('POST', `/api/orders/${encodeURIComponent(id)}/redelivery`, p),
  contactRecipient: (id: string) => req<OrderView>('POST', `/api/orders/${encodeURIComponent(id)}/contact-recipient`, {}),
  approve: (id: string) => req<OrderView>('POST', `/api/orders/${encodeURIComponent(id)}/approve`, {}),
  fix: (id: string, text: string) => req<OrderView>('POST', `/api/orders/${encodeURIComponent(id)}/fix`, { text }),
  feedback: (id: string, p: Record<string, unknown>) => req<{ ok: true; taste: Taste }>('POST', `/api/orders/${encodeURIComponent(id)}/feedback`, p),
  myOrders: (email: string) => req<{ ok: true }>('POST', '/api/web/my-orders', { email }),
  offer: (id: string, ok: boolean) => req<OrderView>('POST', `/api/orders/${encodeURIComponent(id)}/offer`, { ok }),
  support: (p: { topic: string; text: string; orderId?: string; photo?: string; quiet?: boolean }) => req<{ ok: true; id: number }>('POST', '/api/support', p),
  swipe: () => req<{ cards: Record<string, { photo?: string | null; thumb?: string | null; name?: string; desc?: string }> }>('GET', '/api/swipe'),
  gift: (token: string) => req<{ status: string; recipientName: string | null; date: string; slotFrom: number | null; slotTo: number | null; picked: boolean; slots: Slot[] }>('GET', `/api/gift/${token}`),
  giftPick: (token: string, p: Record<string, unknown>) => req<{ status: string; picked: boolean }>('POST', `/api/gift/${token}`, p),
};
